import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router";
import {
  sessionRecordingChoice,
  useSessionRecordingChoice,
  type SessionRecordingChoice,
} from "_shared/session-recording/session-recording-choice";
import { SESSION_RECORDING_UNMASK_CLASS } from "_shared/session-recording/session-recording-privacy";

type Props = {
  choice?: SessionRecordingChoice;
};

/**
 * Asks a signed-in visitor whether FullStory may record their visits, before it records anything.
 *
 * Nothing is recorded before sign-in, so the question waits until then and the login form never
 * has a banner over it. It is not a dialog: a visitor who ignores it can go on using the app, and
 * nothing is recorded while the question stands.
 *
 * It sits at the top of the signed-in shell beside the system notification banners, in the flow,
 * so it takes room rather than covering anything. A sticky banner at the foot of the window hid the
 * assistant bar below `lg`, and the shell's rule is that nothing floats over a page.
 *
 * The wording is the short first layer of the notice; the Privacy Policy carries the detail. It
 * still names three things a policy link cannot stand in for: that this is session replay, that it
 * uses cookies, and that it is off until accepted. Consent to a description vaguer than that is
 * not consent to session replay.
 */
export default function SessionRecordingConsentBanner(props: Readonly<Props>) {
  const choice = props.choice ?? sessionRecordingChoice;
  const { consent, isOpen, isSignedIn } = useSessionRecordingChoice(choice);

  if (!isOpen || !isSignedIn) return null;

  return (
    <Paper
      component="section"
      aria-labelledby="session-recording-consent-title"
      // Fixed wording and buttons, so a replay can show what was asked and what was chosen.
      className={SESSION_RECORDING_UNMASK_CLASS}
      elevation={0}
      square
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        flexShrink: 0,
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          alignItems: { md: "center" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          marginInline: "auto",
          maxWidth: 1200,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography id="session-recording-consent-title" variant="subtitle1" component="h2">
            Help us improve Attorney Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We&apos;d like to use analytics cookies, including session replay, to see how the app is used
            and fix problems faster. They&apos;re off unless you accept, and you can change this at any time
            from <em>Cookie Preferences</em> at the foot of the page.{" "}
            <Link component={RouterLink} to="/privacy-policy">
              Privacy Policy
            </Link>
          </Typography>
          {consent && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {consent === "granted"
                ? "You accepted analytics cookies on this device."
                : "You declined analytics cookies on this device."}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", flexShrink: 0, flexWrap: "wrap", gap: 1 }}>
          {consent && (
            <Button variant="text" onClick={choice.close}>
              Close
            </Button>
          )}
          {/* Declining is as prominent as accepting: a no that takes more finding is not a free choice. */}
          <Button variant="contained" onClick={choice.decline}>
            Decline
          </Button>
          <Button variant="contained" onClick={choice.accept}>
            Accept
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
